import { useState, useEffect } from "react"
import "./Configurator.css"
// Removed import ConfiguratorData
import Mainlayout from "../../components/Mainlayout"
import MyGlbViewer from "../../components/MyGlbViewer/MyGlbViewer"

const Configurator = () => {
  const [formData, setFormData] = useState({})
  const [selectedImages, setSelectedImages] = useState({})
  const [configData, setConfigData] = useState([])

  // Helper function to get dependent dropdown values based on parent selection
  const getDependentDropdownValues = (item, formData) => {
    if (item.dataType === "dropDownListWithDependency" && item.dropDownValues) {
      const parentFieldName = item.dropDownValues[0]?.parentAttributeName
      const parentValue = formData[parentFieldName]
      
      if (parentValue) {
        const matchingDependency = item.dropDownValues.find(
          dependency => dependency.parentAttributeValue === parentValue
        )
        return matchingDependency ? matchingDependency.values : []
      }
      return []
    }
    return item.dropDownValues || []
  }

  // Helper function to reset dependent fields when parent changes
  const resetDependentFields = (parentFieldName, newValue) => {
    const fieldsToReset = {}
    
    configData.forEach(item => {
      if (item.dataType === "dropDownListWithDependency" && 
          item.dropDownValues && 
          item.dropDownValues[0]?.parentAttributeName === parentFieldName) {
        
        const dependentValues = getDependentDropdownValues(item, { ...formData, [parentFieldName]: newValue })
        if (dependentValues.length > 0) {
          fieldsToReset[item.name] = dependentValues[0]
        } else {
          fieldsToReset[item.name] = ""
        }
      }
    })
    
    return fieldsToReset
  }

  useEffect(() => {
    fetch('/Configurator.json')
      .then(res => res.json())
      .then(data => {
        setConfigData(data)

        const initialData = {}
        const initialImages = {}
        
        data.forEach((item) => {
          if (item.dataType === "dropDownList" && item.dropDownValues && item.dropDownValues.length > 0) {
            initialData[item.name] = item.dropDownValues[0]
          } else if (item.dataType === "dropDownListWithDependency" && item.dropDownValues && item.dropDownValues.length > 0) {
            // For dependent dropdowns, we'll set them after the parent is set
            initialData[item.name] = ""
          } else if (item.dataType === "image" && Array.isArray(item.value) && item.value.length > 0) {
            initialImages[item.name] = 0 // default to first image
          } else if (item.value) {
            initialData[item.name] = Array.isArray(item.value) ? item.value[0] : item.value
          }
        })
        
        setFormData(initialData)
        setSelectedImages(initialImages)
      })
      .catch(err => console.error("Failed to fetch config:", err))
  }, [])

  const handleDropdownChange = (fieldName, value) => {
    setFormData((prev) => {
      const newFormData = {
        ...prev,
        [fieldName]: value,
      }
      
      // Check if this field is a parent field that affects other dropdowns
      const dependentFields = resetDependentFields(fieldName, value)
      
      return {
        ...newFormData,
        ...dependentFields
      }
    })
  }

  const handleImageSelect = (fieldName, index) => {
    setSelectedImages((prev) => ({
      ...prev,
      [fieldName]: index,
    }))
  }

  const renderField = (item, index) => {
    if (item.dataType === "dropDownList" && item.dropDownValues) {
      return (
        <tr key={index} className="spec-row">
          <td className="spec-label">{item.label}</td>
          <td className="spec-value">
            <select
              className="dropdown-select"
              value={formData[item.name] || item.dropDownValues[0]}
              onChange={(e) => handleDropdownChange(item.name, e.target.value)}
            >
              {item.dropDownValues.map((option, i) => (
                <option key={i} value={option}>{option}</option>
              ))}
            </select>
          </td>
          <td className="spec-dimension">{item.dimension && item.dimension !== "null" ? item.dimension : ""}</td>
        </tr>
      )
    }
    
    if (item.dataType === "dropDownListWithDependency" && item.dropDownValues) {
      const dependentValues = getDependentDropdownValues(item, formData)
      const currentValue = formData[item.name] || (dependentValues.length > 0 ? dependentValues[0] : "")
      
      return (
        <tr key={index} className="spec-row">
          <td className="spec-label">{item.label}</td>
          <td className="spec-value">
            <select
              className="dropdown-select"
              value={currentValue}
              onChange={(e) => handleDropdownChange(item.name, e.target.value)}
              disabled={dependentValues.length === 0}
            >
              {dependentValues.length > 0 ? (
                dependentValues.map((option, i) => (
                  <option key={i} value={option}>{option}</option>
                ))
              ) : (
                <option value="">Select series first</option>
              )}
            </select>
          </td>
          <td className="spec-dimension">{item.dimension && item.dimension !== "null" ? item.dimension : ""}</td>
        </tr>
      )
    }
    
    if (item.dataType === "image" && Array.isArray(item.value)) {
      return (
        <tr key={index} className="spec-row">
          <td className="spec-label">{item.label}</td>
          <td className="spec-value">
            <div style={{ display: "flex", gap: 8 }}>
              {item.value.map((img, i) => (
                <img
                  key={i}
                  src={`${window.ENV.SERVER_URL}${img}`}
                  alt={`${item.label} ${i + 1}`}
                  style={{
                    width: 60,
                    height: 60,
                    borderRadius: 8,
                    border: selectedImages[item.name] === i ? "2px solid #007bff" : "1px solid #ccc",
                    cursor: "pointer",
                    boxShadow: selectedImages[item.name] === i ? "0 0 8px #007bff55" : "none",
                  }}
                  onClick={() => handleImageSelect(item.name, i)}
                />
              ))}
            </div>
          </td>
          <td className="spec-dimension">{item.dimension && item.dimension !== "null" ? item.dimension : ""}</td>
        </tr>
      )
    }
    
    // Default: normal field
    return (
      <tr key={index} className="spec-row">
        <td className="spec-label">{item.label}</td>
        <td className="spec-value">
          {item.dataType === "string" ? (
            <span className="string-value">{item.value || ""}</span>
          ) : (
            <span>{item.value || ""}</span>
          )}
        </td>
        <td className="spec-dimension">{item.dimension && item.dimension !== "null" ? item.dimension : ""}</td>
      </tr>
    )
  }

  return (
    <Mainlayout>
      <div className="product-detail-border-wrapper">
        <div className="product-detail-container">
          <div className="specifications-panel">
            <table className="spec-table">
              <tbody>{configData.map((item, index) => renderField(item, index))}</tbody>
            </table>
          </div>
          <div className="viewer-panel">
           
            <div className="viewer-container">
              <MyGlbViewer
                modelUrl="/Gear.glb"
                canvasWidth="100%"
                canvasHeight="100%"
                open={true}
              />
            </div>
          </div>
        </div>
      </div>
    </Mainlayout>
  )
}

export default Configurator
